import React, { useEffect, useImperativeHandle, useRef, useState } from "react";
import { DenseIcon } from "./dense-icon";
import { Divider, Grow, Menu, MenuItem, MenuItemProps, Typography, useTheme } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import ArrowRight from '@mui/icons-material/ArrowRight';
import clsx from 'clsx';
import { DividerProps } from "@mui/material/Divider/Divider";

const useStyles = makeStyles((theme) => ({
  subMenuRoot: (props: {open: boolean}) => ({
    justifyContent: "space-between",
    backgroundColor: props.open
      ? theme.palette.action.hover
      : 'rgba(0,0,0,0)'
  }),
  subMenuLabel: {
    display: "flex",
    alignItems: "center"
  }
}));

interface NestedMenuItemProps extends Omit<MenuItemProps, 'button'> {
  parentMenuOpen: boolean
  label?: React.ReactNode
  rightIcon?: React.ReactNode
  dense: boolean,
  ContainerProps?: React.HTMLAttributes<HTMLElement> & React.RefAttributes<HTMLElement | null>
  button?: true | undefined
}

/**
 * https://github.com/azmenak/material-ui-nested-menu-item
 * Modifications have been made to close submenu when open is false.
 * This is required because we use keepMounted on all of our menus. Also
 * modified the right arrow to be better aligned
**/
const NestedMenuItem = React.forwardRef((props: NestedMenuItemProps, ref) => {
  const {
    parentMenuOpen,
    label,
    rightIcon = <ArrowRight />,
    dense = false,
    children,
    className,
    tabIndex: tabIndexProp,
    ContainerProps: ContainerPropsProp = {},
    ...MenuItemProps
  } = props;

  const {ref: containerRefProp, ...ContainerProps} = ContainerPropsProp;

  const menuItemRef = useRef<HTMLLIElement>(null);
  useImperativeHandle(ref, () => menuItemRef.current);

  const containerRef = useRef<HTMLDivElement>(null);
  useImperativeHandle(containerRefProp, () => containerRef.current);

  const menuContainerRef = useRef<HTMLDivElement>(null);

  const [isSubMenuOpen, setIsSubMenuOpen] = useState(false);

  const handleMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
    setIsSubMenuOpen(true);

    if (ContainerProps?.onMouseEnter) {
      ContainerProps.onMouseEnter(event);
    }
  };

  const handleMouseLeave = (event: React.MouseEvent<HTMLElement>) => {
    setIsSubMenuOpen(false);

    if (ContainerProps?.onMouseLeave) {
      ContainerProps.onMouseLeave(event);
    }
  };

  // Check if any immediate children are active
  const isSubmenuFocused = () => {
    const active = containerRef.current?.ownerDocument?.activeElement;
    for (const child of menuContainerRef.current?.children ?? []) {
      if (child === active) {
        return true;
      }
    }
    return false;
  };

  const handleFocus = (event: React.FocusEvent<HTMLElement>) => {
    if (event.target === containerRef.current) {
      setIsSubMenuOpen(true);
    }

    if (ContainerProps?.onFocus) {
      ContainerProps.onFocus(event);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      return;
    }

    if (isSubmenuFocused()) {
      event.stopPropagation();
    }

    const active = containerRef.current?.ownerDocument?.activeElement;

    if (event.key === 'ArrowLeft' && isSubmenuFocused()) {
      containerRef.current?.focus();
    }

    if (
      event.key === 'ArrowRight' &&
      event.target === containerRef.current &&
      event.target === active
    ) {
      const firstChild = menuContainerRef.current?.children[0] as
        | HTMLElement
        | undefined;
      firstChild?.focus();
    }
  };

  /** Custom Code */
  useEffect(() => {
    setIsSubMenuOpen(false);
  }, [parentMenuOpen]);

  const open = isSubMenuOpen && parentMenuOpen;
  const classes = useStyles({open});

  // Root element must have a `tabIndex` attribute for keyboard navigation
  let tabIndex;
  if (!props.disabled) {
    tabIndex = tabIndexProp !== undefined ? tabIndexProp : -1;
  }

  return (
    <div
      {...ContainerProps}
      ref={containerRef}
      onFocus={handleFocus}
      tabIndex={tabIndex}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onKeyDown={handleKeyDown}
    >
      <MenuItem
        {...MenuItemProps}
        className={clsx(classes.subMenuRoot, className)}
        ref={menuItemRef}
      >
        {/** Custom Code */}
        <div className={classes.subMenuLabel}>{label}</div>
        <div className={classes.subMenuLabel}>{rightIcon}</div>
      </MenuItem>

      <Menu
        // Set pointer events to 'none' to prevent the invisible Popover div
        // from capturing events for clicks and hovers
        style={{pointerEvents: 'none'}}
        anchorEl={menuItemRef.current}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        MenuListProps={{dense}}
        open={open}
        autoFocus={false}
        keepMounted
        disableAutoFocus
        disableEnforceFocus
        onClose={() => {
          setIsSubMenuOpen(false);
        }}
      >
        <div ref={menuContainerRef} style={{pointerEvents: 'auto'}}>
          {children}
        </div>
      </Menu>
    </div>
  );
});

interface NestedMenuProps {
  parentMenuOpen: boolean;
  text: string;
  ref?: any;
  icon: any;
  children: React.ReactNode;
}

export const NestedMenu = React.forwardRef((props: NestedMenuProps, ref) => {
  const {parentMenuOpen, text, icon} = props;

  return (
    <NestedMenuItem
      dense
      label={
        <React.Fragment>
          <DenseIcon>{icon}</DenseIcon>
          <Typography variant="inherit">{text}</Typography>
        </React.Fragment>
      }
      ref={ref}
      parentMenuOpen={parentMenuOpen}
    >
      {props.children}
    </NestedMenuItem>
  );
});

export const MenuDivider = React.forwardRef((props: DividerProps, ref) => {
  return (
    <Divider {...props} style={{
      margin: "4px 16px"
    }}/>
  );
});

interface DenseMenuProps {
  open: boolean;
  onClose(): void;
  onEnter?(): void;
  anchorPosition: {left: number, top: number};
  keepMounted?: boolean;
  children: React.ReactNode;
}

export const DenseMenu = React.forwardRef((props: DenseMenuProps, ref) => {
  const {keepMounted=true, children, onEnter, ...other} = props;

  const theme = useTheme();

  const {enteringScreen} = theme.transitions.duration;

  const transitionProps = {
    timeout: {
      enter: enteringScreen,
      exit: 0
    },
    ...(onEnter) && {onEnter}
  };

  return (
    <Menu
      keepMounted={keepMounted}
      anchorReference="anchorPosition"
      MenuListProps={{dense: true}}
      TransitionComponent={Grow}
      TransitionProps={transitionProps}
      {...other}
    >
      {children}
    </Menu>
  );
});

